import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  ArrowRight, ArrowLeft, Loader2, Building2, CheckCircle2, AlertTriangle,
  DollarSign, Sparkles, MapPin, Mail, Send,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const PROPERTY_TYPES = [
  { id: 'hotel', label: 'Hotel / Resort' },
  { id: 'full-service-restaurant', label: 'Full Service Restaurant' },
  { id: 'qsr', label: 'Quick Service Restaurant (QSR)' },
  { id: 'fec', label: 'Family Entertainment Center (FEC)' },
  { id: 'recreation', label: 'Recreation' },
  { id: 'entertainment', label: 'Entertainment Venue' },
  { id: 'retail', label: 'Retail' },
  { id: 'other', label: 'Other' },
];

const SERVICE_LEVELS = [
  { id: 'luxury', label: 'Luxury / Upper Upscale' },
  { id: 'full', label: 'Full Service' },
  { id: 'select', label: 'Select Service' },
  { id: 'economy', label: 'Economy / Extended Stay' },
];

const ANCILLARY = [
  { id: 'fb', label: 'Food & Beverage' },
  { id: 'meetings', label: 'Meetings & Events' },
  { id: 'spa', label: 'Spa / Wellness' },
  { id: 'valet', label: 'Valet / Parking' },
  { id: 'entertainment', label: 'Entertainment / Activities' },
  { id: 'retail', label: 'Retail / Gift Shop' },
];

const PRIORITY_COLORS = { critical: 'bg-red-100 text-red-700 border-red-200', high: 'bg-amber-100 text-amber-700 border-amber-200', medium: 'bg-blue-100 text-blue-700 border-blue-200' };
const WL_PRIMARY = 'var(--wl-primary, #4F6DF5)';
const WL_ACCENT = 'var(--wl-accent, #6C5CE7)';

export default function PropertyWizard({ token, onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [form, setForm] = useState({
    property_name: '', property_type: 'hotel', service_level: 'select',
    total_guestrooms: 120, number_of_buildings: 1, front_desk_workstations: 2,
    annual_reservations: 25000, housekeeping_staff: 15,
    offers_digital_key: false,
    fb_outlet_count: 0, fb_pos_terminals: 0, fb_kitchen_stations: 0,
    spa_treatment_rooms: 0, event_space_sqft: 0, meeting_room_count: 0,
    sales_catering_staff: 0, parking_spaces: 0,
    ancillary_operations: [],
  });
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const toggleAncillary = (id) => setForm(p => ({
    ...p, ancillary_operations: p.ancillary_operations.includes(id)
      ? p.ancillary_operations.filter(x => x !== id)
      : [...p.ancillary_operations, id],
  }));

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND}/api/wizard/analyze`, form, { headers });
      setResults(res.data);
      setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || 'Analysis failed'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND}/api/wizard/apply`, form, { headers });
      toast.success(`Property ${res.data.status}!`);
      if (onComplete) onComplete(res.data.property_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const emailReport = async () => {
    if (!emailTo) { toast.error('Enter a recipient email'); return; }
    setEmailing(true);
    try {
      await axios.post(`${BACKEND}/api/wizard/email-report`, {
        recipient_email: emailTo,
        property_name: results.property_name,
        property_type: results.property_type,
        service_level: results.service_level || '',
        recommendations: results.recommendations,
        summary: results.summary,
      }, { headers });
      toast.success(`Report emailed to ${emailTo}`);
      setEmailSent(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    finally { setEmailing(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="property-wizard">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3, 4, 5].map(s => (
          <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${s > step ? 'bg-muted' : ''}`} style={s <= step ? { backgroundColor: WL_PRIMARY } : undefined} />
        ))}
      </div>

      {/* Step 1: Property Basics */}
      {step === 1 && (
        <div className="space-y-6">
          <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Tell us about your property</h2><p className="text-sm text-muted-foreground mt-1">We'll recommend the right technology platforms based on your property profile.</p></div>
          <div><Label>Property Name</Label><Input value={form.property_name} onChange={e => setForm(p => ({ ...p, property_name: e.target.value }))} placeholder="Oceanview Resort & Spa" className="mt-1" /></div>
          <div><Label>Property Type</Label><div className="grid grid-cols-2 gap-2 mt-1">{PROPERTY_TYPES.map(t => (
            <button key={t.id} onClick={() => setForm(p => ({ ...p, property_type: t.id }))} className={`p-3 rounded-lg border text-left text-sm ${form.property_type === t.id ? 'border-[color:var(--wl-primary,#4F6DF5)] bg-[color:var(--wl-primary,#4F6DF5)]/5' : 'hover:bg-muted/60'}`} style={form.property_type === t.id ? { borderColor: WL_PRIMARY, backgroundColor: `color-mix(in srgb, ${WL_PRIMARY} 5%, transparent)` } : undefined}>{t.label}</button>
          ))}</div></div>
          {form.property_type === 'hotel' && (
            <div><Label>Service Level</Label><div className="grid grid-cols-2 gap-2 mt-1">{SERVICE_LEVELS.map(s => (
              <button key={s.id} onClick={() => setForm(p => ({ ...p, service_level: s.id }))} className={`p-3 rounded-lg border text-left text-sm ${form.service_level === s.id ? '' : 'hover:bg-muted/60'}`} style={form.service_level === s.id ? { borderColor: WL_PRIMARY, backgroundColor: `color-mix(in srgb, ${WL_PRIMARY} 5%, transparent)` } : undefined}>{s.label}</button>
            ))}</div></div>
          )}
          <Button onClick={() => setStep(2)} className="gap-2" disabled={!form.property_name}>Next: Size & Scale <ArrowRight className="h-4 w-4" /></Button>
        </div>
      )}

      {/* Step 2: Size & Scale */}
      {step === 2 && (
        <div className="space-y-6">
          <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Size & Scale</h2><p className="text-sm text-muted-foreground mt-1">These numbers drive platform recommendations and cost estimates.</p></div>
          {form.property_type === 'hotel' && (<>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Total Guest Rooms</Label><Input type="number" value={form.total_guestrooms} onChange={e => setForm(p => ({ ...p, total_guestrooms: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label>Buildings</Label><Input type="number" value={form.number_of_buildings} onChange={e => setForm(p => ({ ...p, number_of_buildings: parseInt(e.target.value) || 1 }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Front Desk Stations</Label><Input type="number" value={form.front_desk_workstations} onChange={e => setForm(p => ({ ...p, front_desk_workstations: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label>Annual Reservations</Label><Input type="number" value={form.annual_reservations} onChange={e => setForm(p => ({ ...p, annual_reservations: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label>Housekeeping Staff</Label><Input type="number" value={form.housekeeping_staff} onChange={e => setForm(p => ({ ...p, housekeeping_staff: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
            </div>
          </>)}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={() => setStep(3)} className="gap-2 flex-1">Next: Operations <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Step 3: Ancillary Operations */}
      {step === 3 && (
        <div className="space-y-6">
          <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Operations & Amenities</h2><p className="text-sm text-muted-foreground mt-1">Select all that apply — each adds relevant technology recommendations.</p></div>
          <div className="grid grid-cols-2 gap-2">{ANCILLARY.map(a => (
            <div key={a.id} role="button" tabIndex={0} onClick={() => toggleAncillary(a.id)} onKeyDown={e => e.key === 'Enter' && toggleAncillary(a.id)} className={`p-3 rounded-lg border text-left text-sm flex items-center gap-2 cursor-pointer ${form.ancillary_operations.includes(a.id) ? '' : 'hover:bg-muted/60'}`} style={form.ancillary_operations.includes(a.id) ? { borderColor: WL_PRIMARY, backgroundColor: `color-mix(in srgb, ${WL_PRIMARY} 5%, transparent)` } : undefined}>
              <Checkbox checked={form.ancillary_operations.includes(a.id)} /> {a.label}
            </div>
          ))}</div>
          {form.ancillary_operations.includes('fb') && (
            <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-muted/30">
              <div><Label className="text-xs">F&B Outlets</Label><Input type="number" value={form.fb_outlet_count} onChange={e => setForm(p => ({ ...p, fb_outlet_count: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label className="text-xs">POS Terminals</Label><Input type="number" value={form.fb_pos_terminals} onChange={e => setForm(p => ({ ...p, fb_pos_terminals: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label className="text-xs">Kitchen Stations</Label><Input type="number" value={form.fb_kitchen_stations} onChange={e => setForm(p => ({ ...p, fb_kitchen_stations: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
            </div>
          )}
          {form.ancillary_operations.includes('spa') && (
            <div className="p-4 rounded-lg bg-muted/30"><Label className="text-xs">Spa Treatment Rooms</Label><Input type="number" value={form.spa_treatment_rooms} onChange={e => setForm(p => ({ ...p, spa_treatment_rooms: parseInt(e.target.value) || 0 }))} className="mt-1 max-w-[120px]" /></div>
          )}
          {form.ancillary_operations.includes('meetings') && (
            <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-muted/30">
              <div><Label className="text-xs">Event Space (sqft)</Label><Input type="number" value={form.event_space_sqft} onChange={e => setForm(p => ({ ...p, event_space_sqft: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label className="text-xs">Meeting Rooms</Label><Input type="number" value={form.meeting_room_count} onChange={e => setForm(p => ({ ...p, meeting_room_count: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              <div><Label className="text-xs">Sales Staff</Label><Input type="number" value={form.sales_catering_staff} onChange={e => setForm(p => ({ ...p, sales_catering_staff: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={() => setStep(4)} className="gap-2 flex-1">Next: Review <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Step 4: Review & Analyze */}
      {step === 4 && (
        <div className="space-y-6">
          <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Review & Analyze</h2><p className="text-sm text-muted-foreground mt-1">Confirm your details, then we'll generate your technology recommendations.</p></div>
          <div className="p-4 rounded-lg border bg-card space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Property</span><span className="font-medium">{form.property_name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Type</span><span>{form.property_type} {form.service_level && `/ ${form.service_level}`}</span></div>
            {form.total_guestrooms > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Rooms</span><span>{form.total_guestrooms}</span></div>}
            {form.fb_outlet_count > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">F&B Outlets</span><span>{form.fb_outlet_count} ({form.fb_pos_terminals} terminals)</span></div>}
            {form.spa_treatment_rooms > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Spa</span><span>{form.spa_treatment_rooms} treatment rooms</span></div>}
            {form.ancillary_operations.length > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Operations</span><span>{form.ancillary_operations.join(', ')}</span></div>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(3)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={analyze} className="gap-2 flex-1" style={{ background: `linear-gradient(to right, ${WL_PRIMARY}, ${WL_ACCENT})` }} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Recommendations
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: Results */}
      {step === 5 && results && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Technology Recommendations</h2><p className="text-sm text-muted-foreground mt-1">{results.property_name} — {results.property_type} {results.service_level && `/ ${results.service_level}`}</p></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{results.summary.total_recommended}</div><div className="text-[10px] text-muted-foreground">Recommended</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{results.summary.critical}</div><div className="text-[10px] text-muted-foreground">Critical</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{results.summary.already_deployed}</div><div className="text-[10px] text-muted-foreground">Deployed</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: WL_PRIMARY }}>${(results.summary.estimated_monthly_total || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Est. Monthly</div></div>
          </div>

          {['critical', 'high', 'medium'].map(priority => {
            const recs = (results.recommendations || []).filter(r => r.priority === priority);
            if (recs.length === 0) return null;
            return (
              <div key={priority}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{priority} Priority ({recs.length})</h3>
                <div className="space-y-1">
                  {recs.map((r, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${r.deployed ? 'bg-emerald-50/50 border-emerald-200' : 'bg-card'}`}>
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.category_color }} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{r.platform_name}</span>
                        <span className="text-[10px] text-muted-foreground ml-2">{r.category_name}</span>
                      </div>
                      {r.deployed ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Deployed</Badge> : <Badge className={`text-[9px] ${PRIORITY_COLORS[priority]}`}>Gap</Badge>}
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono">${r.estimated_monthly.toLocaleString()}/mo</div>
                        <div className="text-[9px] text-muted-foreground">{r.pricing_range}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <Separator />
          <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium"><Mail className="h-4 w-4" /> Email This Report</div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="stakeholder@company.com"
                value={emailTo}
                onChange={e => { setEmailTo(e.target.value); setEmailSent(false); }}
                className="flex-1"
                data-testid="wizard-email-input"
              />
              <Button onClick={emailReport} disabled={emailing || emailSent || !emailTo} className="gap-1.5 shrink-0" variant={emailSent ? 'outline' : 'default'} data-testid="wizard-email-send-btn">
                {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : emailSent ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Send className="h-3.5 w-3.5" />}
                {emailSent ? 'Sent' : 'Send'}
              </Button>
            </div>
            {emailSent && <p className="text-xs text-emerald-600">Report sent to {emailTo}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Start Over</Button>
            <Button onClick={save} className="gap-2 flex-1" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save Property & Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
